import { CSSProperties, ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Box } from "@cloudscape-design/components";

/**
 * Renders an agent message written in markdown.
 *
 * The agent replies with GFM — mostly tables plus short prose — so remark-gfm is
 * enabled for table, strikethrough and autolink support.
 *
 * react-markdown builds React elements rather than injecting HTML, so agent output
 * cannot inject markup. Raw HTML in the markdown is ignored (no rehype-raw).
 */

// Props react-markdown passes to element overrides. Typed locally so the
// component map does not depend on the library's internal prop types.
type NodeProps = { children?: ReactNode };
type CodeProps = NodeProps & { className?: string };
type LinkProps = NodeProps & { href?: string };

const styles: Record<string, CSSProperties> = {
  table: {
    borderCollapse: "collapse",
    width: "100%",
    fontSize: "var(--font-size-body-s, 0.875rem)",
    margin: "0 0 0.75rem",
    display: "block",
    overflowX: "auto",
  },
  cell: {
    border: "1px solid var(--color-border-divider-default, #d1d5db)",
    padding: "0.25rem 0.5rem",
    textAlign: "left",
    verticalAlign: "top",
  },
  headerCell: {
    border: "1px solid var(--color-border-divider-default, #d1d5db)",
    padding: "0.25rem 0.5rem",
    textAlign: "left",
    background: "var(--color-background-table-header, #f2f3f3)",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  code: {
    background: "var(--color-background-code-editor-status-bar, #f4f4f4)",
    padding: "0.05rem 0.25rem",
    borderRadius: 3,
    fontFamily: "var(--font-family-monospace, monospace)",
    fontSize: "0.875em",
  },
  pre: {
    background: "var(--color-background-code-editor-status-bar, #f4f4f4)",
    padding: "0.5rem",
    borderRadius: 4,
    overflowX: "auto",
    margin: "0 0 0.75rem",
  },
  list: { margin: "0 0 0.75rem", paddingInlineStart: "1.25rem" },
};

export default function MarkdownMessage({ text }: { text: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }: NodeProps) => (
          <Box variant="p" padding={{ bottom: "xs" }}>
            {children}
          </Box>
        ),
        h1: ({ children }: NodeProps) => <Box variant="h3">{children}</Box>,
        h2: ({ children }: NodeProps) => <Box variant="h3">{children}</Box>,
        h3: ({ children }: NodeProps) => <Box variant="h4">{children}</Box>,
        h4: ({ children }: NodeProps) => <Box variant="h5">{children}</Box>,
        table: ({ children }: NodeProps) => <table style={styles.table}>{children}</table>,
        th: ({ children }: NodeProps) => <th style={styles.headerCell}>{children}</th>,
        td: ({ children }: NodeProps) => <td style={styles.cell}>{children}</td>,
        ul: ({ children }: NodeProps) => <ul style={styles.list}>{children}</ul>,
        ol: ({ children }: NodeProps) => <ol style={styles.list}>{children}</ol>,
        pre: ({ children }: NodeProps) => <pre style={styles.pre}>{children}</pre>,
        code: ({ children, className }: CodeProps) =>
          // Fenced blocks carry a language class and are wrapped by <pre>.
          className ? (
            <code className={className}>{children}</code>
          ) : (
            <code style={styles.code}>{children}</code>
          ),
        a: ({ children, href }: LinkProps) => (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
}
